import React from 'react';

import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { SearchInventoryStatusDTO } from '@recurrency/core-api-schema/dist/search/getSearchInventoryStatus';

import { FailedValidationRulesPanelContent } from 'components/recipes/sidePane/FailedValidationRulesPanelContent';
import { InventoryUsagePanelContent } from 'components/recipes/sidePane/InventoryUsagePanelContent';
import { ItemLocationNotesPanelContent } from 'components/recipes/sidePane/ItemLocationNotesPanelContent';
import { LocationAvailabilityPanelContent } from 'components/recipes/sidePane/LocationAvailabilityPanelContent';
import { PurchaseOrderLinesPanelContent } from 'components/recipes/sidePane/PurchaseOrderLinesPanelContent';
import { SalesInvoiceLinesPanelContent } from 'components/recipes/sidePane/SalesInvoiceLinesPanelContent';
import { SalesOrderLinesPanelContext } from 'components/recipes/sidePane/SalesOrderLinesPanelContext';
import { SidePane } from 'components/recipes/sidePane/SidePane';
import { StockInheritancePanelContent } from 'components/recipes/sidePane/StockInheritancePanelContent';
import { TopCustomersPanelContext } from 'components/recipes/sidePane/TopCustomersPanelContext';
import { UnitOfMeasuresPanelContent } from 'components/recipes/sidePane/UnitOfMeasuresPanelContent';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { getErpName, splitIdNameStr } from 'utils/formatting';
import { ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { isTenantErpTypeNetSuite } from 'utils/tenants';
import { getTenantSetting, showScheduledReleaseStockFeatures } from 'utils/tenantSettings';

import { ItemNotesPanelContent } from '../PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/ItemNotesPanelContent';
import {
  ScheduledReleaseStockPanelContent,
  ScheduledReleaseStockPanelRecord,
} from '../PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/ScheduledReleaseStockPanelContent';
import { SupplierNotesPanelContent } from '../PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/SupplierNotesPanelContent';

export function PurchaseStatusSidePane({ record }: { record: SearchInventoryStatusDTO | null }) {
  const shouldShowScheduledReleaseStockFeatures = showScheduledReleaseStockFeatures();
  const { activeTenant } = useGlobalApp();
  const { foreignId: locationId, name: locationName } = record
    ? splitIdNameStr(record.location)
    : { foreignId: '', name: '' };

  if (!record) {
    return <SidePane title="" content={<div>Please select a table row</div>} />;
  }

  // hide item notes panel for netsuite since we don't sync item notes for netsuite tenants);
  const showItemNotesPanel = !isTenantErpTypeNetSuite(activeTenant.erpType);
  const showStockInheritance = !!getTenantSetting(TenantSettingKey.FeatureStockInheritance);
  const itemInfo: ScheduledReleaseStockPanelRecord = {
    itemId: record.itemId,
    itemUid: record.itemUid,
    unitOfMeasure: record.unitOfMeasure,
    locationId,
  };

  return (
    <SidePane
      title={`Item #${record.itemId} at ${record.location}`}
      settingKey={ViewSettingKey.PurchaseStatusSidePane}
      description={record.itemName}
      panels={[
        {
          settingKey: 'usage',
          title: 'Usage',
          content: (
            <InventoryUsagePanelContent
              itemId={record.itemId}
              itemUid={record.itemUid}
              locations={[{ foreignId: locationId, name: locationName }]}
            />
          ),
        },
        showStockInheritance
          ? {
              settingKey: 'stockInheritance',
              title: 'Stock Inheritance',
              content: <StockInheritancePanelContent itemId={record.itemId} itemUid={record.itemUid} />,
            }
          : null,
        showItemNotesPanel
          ? {
              settingKey: 'itemNotes',
              title: `${getErpName(activeTenant.erpType)} Item Notes`,
              content: (
                <ItemNotesPanelContent
                  uniqueItems={[{ itemId: record.itemUid, foreignId: record.itemId, name: record.itemName }]}
                />
              ),
            }
          : null,
        {
          settingKey: 'itemLocationNotes',
          title: 'Recurrency Notes',
          content: (
            <ItemLocationNotesPanelContent
              itemLocationTitle={`${record.itemName} at ${record.location}`}
              itemUid={record.itemUid}
              locationId={locationId}
            />
          ),
          optional: true,
        },
        {
          settingKey: 'locationStock',
          title: 'Location Stock',
          content: <LocationAvailabilityPanelContent itemUid={record.itemUid} />,
        },
        {
          settingKey: 'unitOfMeasures',
          title: 'Unit of Measures',
          content: <UnitOfMeasuresPanelContent itemId={record.itemId} />,
        },
        {
          settingKey: 'purchaseOrders',
          title: 'Purchase Orders',
          content: (
            <PurchaseOrderLinesPanelContent
              itemId={record.itemId}
              companyId={record.company_id}
              locationIds={[locationId]}
            />
          ),
        },
        {
          settingKey: 'salesOrder',
          title: 'Sales Orders',
          content: (
            <SalesOrderLinesPanelContext
              itemId={record.itemId}
              locationIds={[locationId]}
              companyId={record.company_id}
            />
          ),
        },
        {
          settingKey: 'salesHistory',
          title: 'Sales History',
          content: (
            <SalesInvoiceLinesPanelContent
              itemId={record.itemId}
              locationIds={[locationId]}
              companyId={record.company_id}
            />
          ),
        },
        {
          settingKey: 'topCustomers',
          title: 'Top Customers (Sales YTD)',
          content: (
            <TopCustomersPanelContext itemId={record.itemId} locationId={locationId} companyId={record.company_id} />
          ),
        },
        shouldShowScheduledReleaseStockFeatures
          ? {
              settingKey: 'scheduledReleases',
              title: 'Open Scheduled Releases',
              content: <ScheduledReleaseStockPanelContent itemInfo={itemInfo} />,
            }
          : null,
        {
          settingKey: 'supplierNotes',
          title: `${getErpName(activeTenant.erpType)} Supplier Notes`,
          content: <SupplierNotesPanelContent uniqueSuppliers={[record.supplier]} />,
        },
        {
          settingKey: 'failedValidationRules',
          title: 'Failed Rules',
          content: <FailedValidationRulesPanelContent itemUid={record.itemUid} locationId={locationId} />,
        },
      ]}
    />
  );
}
